import { useCallback } from 'react';

import qs from 'qs';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useCurrentUserId, useSessionUuid } from 'src/app/shared/hooks/session';
import type { ShareActionsOptions, ShareChannel } from 'src/app/shared/hooks/share';

import type { ShareablePost } from '../../types';

const MEDIUM_MAPPING: Readonly<Partial<Record<ShareChannel, string>>> = {
	facebookMessenger: 'facebook_messenger',
	copy: 'copylink',
};

function getPetitionShareUrl(
	{
		sessionUuid,
		currentUserId,
		slug,
		channel,
		customUtmParams = {},
	}: {
		sessionUuid: string;
		currentUserId?: string;
		slug: string;
		channel: ShareChannel;
		customUtmParams?: Record<string, string | undefined>;
	},
	previewLinkId?: string,
): string {
	const trackParams = qs.stringify(
		{
			recruiter: currentUserId || undefined,
			recruited_by_id: sessionUuid,
			utm_source: 'share_petition',
			utm_campaign: 'petition_dashboard_share_modal',
			// FIXME not sure why the typings don't allow for undefined but we're still handling undefined here

			utm_medium: MEDIUM_MAPPING[channel] || channel || 'default', // 'default' is used for the default case
			...customUtmParams,
		},
		{ encodeValuesOnly: true },
	);
	let path = `/p/${slug}`;
	if (currentUserId) {
		path += `/sfs/${channel}/${currentUserId}`;
		if (previewLinkId) {
			path += `/${previewLinkId}`;
		}
	}

	return `${path}?${trackParams}`;
}

export function useGetDashboardShareUrl(
	previewLinkConfigs?: readonly ShareablePost[],
	customUtmParams?: Array<Record<string, string | undefined>>,
): ShareActionsOptions<ShareChannel>['getUrl'] {
	const sessionUuid = useSessionUuid();
	const currentUserId = useCurrentUserId();
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();

	return useCallback(
		(channel) => {
			const previewLinkId = previewLinkConfigs?.find((config) => config.platform.includes(channel))?.id;
			const utmParams = customUtmParams?.find((params) => params.utm_medium?.includes(channel));
			const commonUrlArgs = {
				sessionUuid,
				currentUserId,
				slug: petition.slug,
				channel,
				customUtmParams: utmParams,
			};

			return getPetitionShareUrl({ ...commonUrlArgs }, previewLinkId);
		},
		[currentUserId, petition.slug, sessionUuid, previewLinkConfigs, customUtmParams],
	);
}
